/**
 * Default Secondary Program content — mirrors fallbacks in SecondaryPage.js
 * Used by CMS forms so fields are never empty on first load.
 */
export const SECONDARY_DEFAULTS = {
  hero: {
    badge: 'Grades 7-12',
    heading: 'Secondary Program',
    subtitle: 'Preparing confident, capable leaders for university and beyond',
    images: [],
  },
  
  hero_stats: [
    { number: '25', label: 'Maximum Class' },
    { number: '2', label: 'Curriculum', sublabel: 'Cambridge + National (SPK Track)' },
    { number: 'LEAPS', label: 'Student Development' },
  ],
  
  overview: {
    heading: 'Secondary Excellence',
    description: 'Permata Harapan Secondary provides a comprehensive education that integrates academic excellence, character formation, and holistic student development. Through the Cambridge and National Curriculum, the 5E Instructional Model, and a structured Character Shaping and Student Development Program (LEAPS), students develop the knowledge, competencies and values required to succeed in higher education and contribute to an increasingly interconnected world.',
  },

  program_highlights: [
    {
      title: 'University Preparation',
      description: 'Rigorous academics aligned with Cambridge IGCSE and A-Level standards for global university readiness.',
      icon: 'cap',
    },
    {
      title: 'Character Shaping Program',
      description: 'A structured Character Shaping Program integrates Eastern Values, Social-Emotional Learning, and Well-Being to cultivate integrity, resilience, compassion, responsibility, and ethical decision-making.',
      icon: 'star',
    },
    {
      title: 'Applied Learning',
      description: 'Students extend their learning through interdisciplinary subjects and research that deepen understanding, encourage intellectual curiosity, and prepare them for future challenges.',
      icon: 'atom',
    },
    {
      title: 'Students Development Program',
      description: 'The LEAPS Framework provides opportunities for leadership, extracurricular involvement, achievement, participation, and service, empowering students to grow as confident, responsible, compassionate, resilient, and globally aware individual.',
      icon: 'globe',
    },
  ],

  pathways: [
    {
      level: 'Lower Secondary (Grades 7-8)',
      title: 'Foundation & Exploration',
      description: 'Students develop broad academic knowledge and skills that prepares them for Cambridge IGCSE.',
      features: [
        'Balanced curriculum',
        'Study skills and self-management',
        'Character and habits building',
      ],
    },
    {
      level: 'Upper Secondary (Grades 9-10)',
      title: 'Specialization & Mastery',
      description: 'Students deepen their learning through subject specialisation and Cambridge IGCSE, preparing for higher education pathways.',
      features: [
        'Subject specialization',
        'IGCSE readiness and rigor',
        'Immersion and real-world exposure',
        'Interdisciplinary project depth',
        'Introduction to career pathways',
      ],
    },
    {
      level: 'Pre University (Grades 11-12)',
      title: 'Advanced Study and University Preparation',
      description: 'Students deepen their learning through A/AS level specialisation, preparing for university admission and life beyond school.',
      features: [
        'Advanced Subject Mastery',
        'University and career counseling',
        'AS/A Level exam readiness',
        'Research and Innovation',
        'Globally ready graduates',
      ],
    },
  ],

  subject_tracks: [
    {
      track: 'Science & Health Track',
      subjects: ['Biology', 'Chemistry', 'Physics'],
      careers: 'Medicine, Pharmacy, Nursing, Biotechnology, Veterinary Science, Public Health, Sport Science',
    },
    {
      track: 'Engineering & Technology Track',
      subjects: ['Physics', 'Additional Mathematics', 'Computer Science'],
      careers: 'Engineering, Software Development, Data Science, Robotics, Architecture',
    },
    {
      track: 'Business & Economics Track',
      subjects: ['Accounting', 'Business Studies', 'Economics'],
      careers: 'Business Management, Finance & Banking, Entrepreneurship, Economics & Public Policy',
    },
    {
      track: 'Creative Arts Track',
      subjects: ['Art & Design', 'Music', 'Drama'],
      careers: 'Graphic Design, Film & Media Production, Performing Arts, Music Production, Fashion Design',
    },
    {
      track: 'Language & Communication Track',
      subjects: ['English (EFL/ESL)', 'Mandarin/German', 'Bahasa Indonesia'],
      careers: 'Tourism & Hospitality, Language Teaching, Content Writing, Cross-Cultural Communication',
    },
    {
      track: 'Humanities & Global Studies Track',
      subjects: ['Humanities and Global Perspective', 'History', 'Geography'],
      careers: 'Law, Political Science, Diplomacy, Social Work, Journalism, International Relations, Public Policy',
    },
    {
      track: 'Sport and Fitness Track',
      subjects: ['Physical Education'],
      careers: 'Physiotherapy, Coaching, Sports Management',
    },
  ],

  support: {
    heading: 'Guidance & Counseling',
    description: 'Comprehensive support for academic, personal, and career development',
    items: [
      {
        title: 'Academic Counseling',
        description: 'One-on-one guidance for course selection, study strategies, and academic goals.',
        icon: 'chart',
      },
      {
        title: 'University Guidance',
        description: 'Expert support for university applications, essays, and scholarship opportunities.',
        icon: 'target',
      },
      {
        title: 'Career Counseling',
        description: 'Career assessments, internships, and connections with industry professionals.',
        icon: 'briefcase',
      },
      {
        title: 'Personal Development',
        description: 'Mentoring, wellbeing support, and character formation programs.',
        icon: 'leaf',
      },
    ],
  },

  achievements: {
    heading: 'Student Success',
    description: 'Results that speak for themselves',
    items: [
      { number: '95%', label: 'University Acceptance Rate' },
      { number: '85%', label: 'Cambridge A*/A Grades' },
      { number: '20+', label: 'University Destinations' },
      { number: '100%', label: 'Graduate Employment/Study' },
    ],
  },

  opportunities: {
    heading: 'Leadership & Enrichment',
    description: 'Opportunities to grow, compete, and make a difference',
    items: [
      'Model United Nations',
      'Science Olympiads',
      'Debate & Public Speaking',
      'Sports Competitions',
      'Arts & Music Performances',
      'Community Service Projects',
      'Student Exchange Programs',
      'Entrepreneurship Club',
    ],
  },

  cta: {
    heading: 'Ready to Begin Your Secondary Journey?',
    description: 'Join a community of ambitious learners preparing for global success',
  },
};

const ARRAY_FIELDS = ['hero_stats', 'program_highlights', 'pathways', 'subject_tracks'];
const OBJECT_FIELDS = ['hero', 'overview', 'support', 'achievements', 'opportunities', 'cta'];

export function mergeSecondaryData(dbData = {}) {
  const merged = { ...SECONDARY_DEFAULTS, ...dbData };

  // For arrays: fall back to defaults if DB has empty or missing array
  ARRAY_FIELDS.forEach((field) => {
    if (!Array.isArray(dbData[field]) || dbData[field].length === 0) {
      merged[field] = SECONDARY_DEFAULTS[field];
    }
  });

  // For nested objects: deep merge (DB values win at top-level keys)
  OBJECT_FIELDS.forEach((field) => {
    if (!dbData[field] || typeof dbData[field] !== 'object') {
      merged[field] = SECONDARY_DEFAULTS[field];
    } else {
      merged[field] = { ...SECONDARY_DEFAULTS[field], ...dbData[field] };
    }
  });

  return merged;
}
